import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import { ValidationHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$helpers';
import { useDidMount, useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { useForm } from '@bcp-channels-sharedcomponents-reactjs/form-utils/dist/$components';
import { FormHookOutput } from '@bcp-channels-sharedcomponents-reactjs/form-utils/dist/$types';
import { FormHelper } from '@bcp-channels-sharedcomponents-reactjs/form-utils/dist/helpers';
import {
	ColorPaletteThemeModel,
	PopupMenuItem,
	PopupMenuRef,
	useTheme,
} from '@bcp-nextgen-dx-component-factory/design-system';
import { ActiveItem } from '@bcp-nextgen-dx-component-factory/design-system';
import { Validations } from '@constants';
import { useBuildPhoneCodes, useReactiveForm, UseReactiveFormInputPropsOutput } from '@hooks';
import { saveIntegratorDataFormInput, saveIntegratorDataFormIsValid, useUserInput } from '@store';
import { Anything, IntegratorData, IntegratorDetail, IntegratorEnum } from '@types';

interface IntegratorFormResources {
	defaultInternationalDialing: string;
	contact: string;
	inputCorporateName: string;
	inputCountry: string;
	inputInterlocutorName: string;
	inputInterlocutorEmail: string;
	inputTechnicalInterlocutorEmail: string;
	inputContact: string;
	required: string;
	invalidEmail: string;
	invalidPhone: string;
}

interface IntegratorFormHelperOutput {
	resources: IntegratorFormResources;
	colorPalette: ColorPaletteThemeModel;
	selectedIntegrator: IntegratorDetail | undefined;
	selectedOtherOrNewIntegrator: boolean;
	form: FormHookOutput;
	phoneCodes: PopupMenuItem[];
	popupMenuCountryRef: React.RefObject<PopupMenuRef>;
	phoneFlagId: number;
	maxLengthContact: number | undefined;
	handlePressIndicative: (id: ActiveItem | undefined) => void;
	getInputProps: (name: string) => UseReactiveFormInputPropsOutput;
	onClearCorporateName: () => void;
	onClearInterlocutorName: () => void;
	onClearInterlocutorEmail: () => void;
	onClearInterlocutorContact: () => void;
}

export const useIntegratorFormHelper = (): IntegratorFormHelperOutput => {
	const t = useTranslation();
	const { colorPalette } = useTheme();
	const dispatch = useDispatch();
	const { forms, selectedIntegrator } = useUserInput();
	const { phoneCodes, initialPhoneId, getSelectedPhone } = useBuildPhoneCodes({ withFlags: true });
	const [phoneFlagId, setPhoneFlagId] = useState<number>(0);
	const popupMenuCountryRef = useRef<PopupMenuRef>(null);

	const resources: IntegratorFormResources = {
		defaultInternationalDialing: t('global.defaultInternationalDialing'),
		contact: t('placeholders.integratorData.contact'),
		inputCorporateName: t('placeholders.integratorData.inputCorporateName'),
		inputCountry: t('general.country'),
		inputInterlocutorName: t('placeholders.integratorData.inputInterlocutorName'),
		inputInterlocutorEmail: t('placeholders.integratorData.inputInterlocutorEmail'),
		inputTechnicalInterlocutorEmail: t('placeholders.integratorData.inputTechnicalInterlocutorEmail'),
		inputContact: t('placeholders.integratorData.inputContact'),
		required: t('general.errors.required'),
		invalidEmail: t('general.errors.invalidEmail'),
		invalidPhone: t('general.errors.invalidPhone'),
	};

	useDidMount(() => {
		doValidation('email');
		popupMenuCountryRef.current?.setActiveItem(initialPhoneId);
		if (initialPhoneId !== undefined) {
			popupMenuCountryRef.current?.setActiveItem(initialPhoneId);
		}
	});

	const saveInputOnStore = (values: Anything) => {
		const data: IntegratorData = {
			info: {
				email: values.interlocutorEmail,
				indicative: values.interlocutorIndicative,
				name: values.interlocutorName,
				phone: values.interlocutorContact,
			},
			corporateName: values.corporateName,
		};
		dispatch(saveIntegratorDataFormInput(data));
	};

	const selectedOtherOrNewIntegrator =
		selectedIntegrator?.code === IntegratorEnum.Other ||
		selectedIntegrator?.code === IntegratorEnum.AddNewIntegrator;

	const form = useForm('integratorData');

	const { isValid, getInputProps, handleOnChangeField, doValidation } = useReactiveForm({
		name: form.name,
		initialValues: {
			corporateName: forms?.integratorData?.corporateName,
			interlocutorName: forms?.integratorData?.info?.name,
			interlocutorEmail: forms?.integratorData?.info?.email,
			interlocutorIndicative: forms?.integratorData?.info?.indicative,
			interlocutorContact: forms?.integratorData?.info?.phone,
		},
		onSubmit: saveInputOnStore,
		validate: (values: Anything) => {
			const isInterlocutorContactPT =
				(phoneCodes[phoneFlagId]?.content as string) === resources.defaultInternationalDialing;
			return {
				corporateName: FormHelper.validateField(values.corporateName, [
					selectedIntegrator?.code === IntegratorEnum.Other
						? ValidationHelper.validateRequired(resources.required)
						: () => undefined,
				]),
				interlocutorName: FormHelper.validateField(values.interlocutorName, [
					selectedOtherOrNewIntegrator
						? ValidationHelper.validateRequired(resources.required)
						: () => undefined,
				]),
				interlocutorEmail: FormHelper.validateField(values.interlocutorEmail, [
					ValidationHelper.validateRequired(resources.required),
					ValidationHelper.validateRegex(Validations.emailValidRegex, resources.invalidEmail),
				]),
				interlocutorContact: FormHelper.validateField(values.interlocutorContact, [
					selectedOtherOrNewIntegrator
						? ValidationHelper.validateRequired(resources.required)
						: () => undefined,
					isInterlocutorContactPT
						? ValidationHelper.validateEqualLength(9, resources.invalidPhone)
						: () => undefined,
				]),
			};
		},
	});

	useEffect(() => {
		doValidation('email');
		popupMenuCountryRef.current?.setActiveItem(0);
	}, [doValidation, selectedOtherOrNewIntegrator]);

	useEffect(() => {
		dispatch(saveIntegratorDataFormIsValid({ isValid }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isValid]);

	const maxLengthContact = useMemo(() => {
		if (phoneCodes[phoneFlagId]?.content === resources.defaultInternationalDialing) {
			return 9;
		} else {
			return undefined;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [phoneFlagId]);

	const clearInput = (inputProps: UseReactiveFormInputPropsOutput) => {
		inputProps.onChangeValue('');
		inputProps.onBlur(true);
	};

	const onClearCorporateName = () => clearInput(getInputProps('corporateName'));
	const onClearInterlocutorName = () => clearInput(getInputProps('interlocutorName'));
	const onClearInterlocutorEmail = () => clearInput(getInputProps('interlocutorEmail'));
	const onClearInterlocutorContact = () => clearInput(getInputProps('interlocutorContact'));

	const handlePressIndicative = (item: ActiveItem | undefined) => {
		if (item?.content !== undefined) {
			const selectedPhone = getSelectedPhone(item.content);
			if (selectedPhone !== undefined && selectedPhone.internationalCode !== undefined) {
				setPhoneFlagId(item.id);
				handleOnChangeField('interlocutorIndicative')(selectedPhone.internationalCode as string);
			}
		}
		popupMenuCountryRef.current?.hide();
	};

	return {
		resources,
		colorPalette,
		form,
		selectedIntegrator,
		selectedOtherOrNewIntegrator,
		phoneCodes,
		popupMenuCountryRef,
		phoneFlagId,
		maxLengthContact,
		getInputProps,
		handlePressIndicative,
		onClearCorporateName,
		onClearInterlocutorName,
		onClearInterlocutorEmail,
		onClearInterlocutorContact,
	};
};
